import { getAccountDisplayName } from '@/utils/accounts/get-account-display-name';
import { isModulrAccount } from '@/utils/accounts/is-modulr-account';
import { FinancialServiceProviderAdapters } from '@seapoint/server/enums';
import { createColumnHelper } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import { AvatarNameDescription } from '../../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { AccountView } from '../../../../types/entity-types';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { FromDateFormField } from './from-date-form-field';
import { OpenBankingAccountDisclaimer } from './open-banking-account-disclaimer';
import { XeroAccountIdFormField } from './xero-account-id-form-field';

const columnHelper = createColumnHelper<AccountView>();

export const accountMappingTableColumnsDefinition = [
  columnHelper.accessor((row) => row.institution?.name, {
    id: 'accountName',
    header: 'Account',
    cell: ({ row }) => {
      const name = getAccountDisplayName(row.original);
      const formattedBalance = getAmountWithCurrency({
        amount: row.original.totalBalance ?? 0,
        currency: row.original.currency,
        isAccountBalance: true,
      });
      const description = `Balance ${formattedBalance}`;

      return (
        <AvatarNameDescription
          avatar={{ showAvatar: true, avatarUrl: row.original.institution?.logo, size: 'xs' }}
          name={name}
          description={description}
        />
      );
    },
    sortingFn: (rowA, rowB) => {
      const isModulrA = isModulrAccount(rowA.original);
      const isModulrB = isModulrAccount(rowB.original);

      if (isModulrA && !isModulrB) {
        return -1;
      }

      if (!isModulrA && isModulrB) {
        return 1;
      }

      return 0;
    },
    size: 30,
  }),
  columnHelper.display({
    id: 'rightArrow',
    cell: () => (
      <div className="h-[30px] w-[30px] rounded-full bg-white p-1.5">
        <ChevronRight className="h-full w-full text-muted-foreground" />
      </div>
    ),
    size: 5,
    minSize: 5,
  }),
  columnHelper.display({
    id: 'xeroAccountName',
    header: 'Xero Account',
    cell: ({ row, table }) => {
      const allXeroBankAccounts = table.options.meta?.xeroBankAccounts ?? [];

      const matchingCurrencyExistingXeroBankAccounts = allXeroBankAccounts.filter(
        (a) => a.type === 'existing' && a.currency === row.original.currency,
      );

      // Only show "Create new account" option for Modulr accounts
      // Non-Modulr accounts (Yapily, Investment, etc.) can only map to existing Xero accounts
      const isModulrAccount = row.original.fspAdapter === FinancialServiceProviderAdapters.MODULR;
      const xeroCreateAccountOptions = isModulrAccount ? allXeroBankAccounts.filter((a) => a.type === 'create') : [];

      return (
        <XeroAccountIdFormField
          row={row}
          xeroBankAccounts={[...matchingCurrencyExistingXeroBankAccounts, ...xeroCreateAccountOptions]}
          isCreatingXeroAccount={table.options.meta?.isPrimaryActionLoading ?? false}
          onCreateXeroAccount={() =>
            (table.options.meta?.onRowPrimaryAction?.(row.original.id) ?? Promise.resolve(undefined)) as Promise<
              string | undefined
            >
          }
        />
      );
    },
    size: 40,
  }),
  columnHelper.display({
    id: 'importDate',
    header: 'Import transactions from',
    cell: ({ row }) => {
      if (row.original.fspAdapter !== FinancialServiceProviderAdapters.MODULR) {
        // Show info box for non-Modulr accounts (Yapily, Investment, etc.)
        // Pass row index for alternating background colors that contrast with table stripes
        return <OpenBankingAccountDisclaimer />;
      }

      return <FromDateFormField row={row} />;
    },
    size: 25,
  }),
];
